import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Logo } from 'foundations-components/transitional/components/Logo';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import unescapedText from 'I18n/utils/unescapedText';
import { useDebounceValue } from 'marketplace-ui-common/hooks/useDebounceValue';
import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { useTrackMarketplaceEvent } from 'marketplace-ui-tracking';
import { getMarketplaceInteractionEvent, SearchResultViewTracker } from 'marketplace-ui-tracking/events/marketplace';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AUTOCOMPLETE_QUERY as NEW_PUBLIC_AUTOCOMPLETE_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationPublicRpc.dfc.new';
import { AUTOCOMPLETE_QUERY as NEW_AUTOCOMPLETE_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationRpc.dfc.new';
import { SEARCH_QUERY_CHAR_LIMIT } from '../../constants/searchResults';
import { usePersonalizationQuery } from '../../hooks/usePersonalizationQuery';
import { useTrackAutocompleteSearchResultEvent2 } from '../../hooks/useTrackAutocompleteSearchResultEvent';
import { useProductTypeSearchParam } from '../../hooks/useProductTypeSearchParams';
import { mapAutocompleteItem } from '../utils/mapAutocompleteItem';
import { buildQuickSearchUrl } from '../utils/navigation';
import { useGates } from '../../hooks/useGates';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HANDLED_KEYS = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'];
export const getOptionId = i => `search-bar-dropdown-option-${i}`;
const LISTBOX_ID = 'search-bar-dropdown-listbox';
export function SearchBarContents({
  productTypes,
  resultTypes
}) {
  var _searchParams$get;
  const history = useHistory();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const searchParams = useSearchParams();
  const [searchTerm, setSearchTerm] = useState((_searchParams$get = searchParams.get('search')) !== null && _searchParams$get !== void 0 ? _searchParams$get : '');
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const productType = useProductTypeSearchParam();
  const debouncedSearchTerm = useDebounceValue(searchTerm, 300);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const gates = useGates();
  const {
    error
  } = usePersonalizationQuery({
    app: NEW_AUTOCOMPLETE_QUERY,
    public: NEW_PUBLIC_AUTOCOMPLETE_QUERY,
    options: {
      skip: !debouncedSearchTerm,
      variables: {
        fallbackLanguage: 'en',
        limit: 8,
        productTypes: productTypes !== null && productTypes !== void 0 ? productTypes : gates.solutionsSpringSpotlight ? ['APP', 'AGENT', 'SOLUTIONS_PARTNER_PROFILE'] : ['APP', 'AGENT'],
        queryTerm: debouncedSearchTerm,
        resultTypes,
        useSpellingCorrections: true
      },
      onCompleted: data => {
        trackMarketplaceEvent(getMarketplaceInteractionEvent({
          action: 'search bar searched',
          search: searchTerm
        }));
        setActiveIndex(-1);
        setResults(data.result.results.map(result => mapAutocompleteItem(result, history.location.pathname)));
        if (data.result.results.length === 1) {
          trackMarketplaceEvent(getMarketplaceInteractionEvent({
            action: 'search dead end reached',
            search: debouncedSearchTerm,
            type: productType
          }));
        }
      }
    }
  });
  const closeDropdown = () => {
    setOpen(false);
    setActiveIndex(-1);
  };

  // close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        closeDropdown();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);
  const {
    getAutocompleteSearchResultEventProps,
    trackAutocompleteSearchResultClickedEvent
  } = useTrackAutocompleteSearchResultEvent2({
    resultCount: results.length,
    searchTerm
  });
  const handleResultSelect = (item, resultIndex) => {
    var _inputRef$current;
    setSearchTerm(item.base.resultType === 'QUERY' ? item.base.term : '');
    trackAutocompleteSearchResultClickedEvent(item.base, resultIndex !== null && resultIndex !== void 0 ? resultIndex : activeIndex);
    history.push(item.url);
    closeDropdown();
    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.blur();
  };
  const moveDown = () => {
    setActiveIndex(prev => {
      const newIndex = (prev + 1) % results.length;
      return newIndex;
    });
  };
  const moveUp = () => {
    setActiveIndex(prev => {
      const newIndex = (prev - 1 + results.length) % results.length;
      return newIndex;
    });
  };
  const handleKeyDown = e => {
    var _inputRef$current2, _inputRef$current3;
    if (HANDLED_KEYS.includes(e.key)) e.preventDefault();
    switch (e.key) {
      case 'ArrowDown':
        if (results.length > 0) moveDown();
        break;
      case 'Tab':
        if (results.length > 0) {
          if (e.shiftKey) {
            moveUp();
          } else {
            moveDown();
          }
        }
        break;
      case 'ArrowUp':
        if (results.length > 0) moveUp();
        break;
      case 'Enter':
        // an item is highlighted - so "click" that instead
        if (activeIndex >= 0) {
          handleResultSelect(results[activeIndex], activeIndex);
          break;
        }
        // else - search by the contents of the searchbar

        if (!searchTerm.trim()) break;
        trackMarketplaceEvent(getMarketplaceInteractionEvent({
          action: 'search submitted',
          search: searchTerm
        }));
        history.push(buildQuickSearchUrl(searchTerm, history.location.pathname));
        closeDropdown();
        (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.blur();
        break;
      case 'Escape':
        closeDropdown();
        if (!searchTerm) (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 || _inputRef$current3.blur();
        break;
      default:
        break;
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    ref: containerRef,
    style: {
      width: '100%',
      position: 'relative'
    },
    onKeyDown: handleKeyDown,
    children: [/*#__PURE__*/_jsx(SearchInput, {
      "aria-activedescendant": activeIndex >= 0 ? getOptionId(activeIndex) : undefined,
      "aria-controls": results.length > 0 ? LISTBOX_ID : undefined,
      "aria-expanded": results.length > 0,
      "data-test-id": "storefront-search-input",
      inputRef: inputRef,
      maxLength: SEARCH_QUERY_CHAR_LIMIT,
      onChange: e => {
        setSearchTerm(e.target.value);
        if (!e.target.value) {
          setResults([]);
          setActiveIndex(-1);
        }
      },
      onFocus: () => setOpen(true),
      placeholder: unescapedText('marketplaceStorefront.searchBar.placeholder'),
      value: searchTerm
    }), open && results.length > 0 && /*#__PURE__*/_jsxs(CardWrapper, {
      borderRadius: "400",
      insetInlineEnd: '0',
      insetInlineStart: '0',
      marginBlockStart: "100",
      overflow: "hidden",
      position: "absolute",
      zIndex: "300",
      children: [error && /*#__PURE__*/_jsx(ErrorMessage, {
        type: "badRequest"
      }), !error && /*#__PURE__*/_jsx(List, {
        "data-test-id": "search-results-list",
        role: "listbox",
        id: LISTBOX_ID,
        paddingBlock: "200",
        children: results.map((result, index) => /*#__PURE__*/_jsx(SearchResultViewTracker, Object.assign({}, getAutocompleteSearchResultEventProps(result.base, index), {
          children: /*#__PURE__*/_jsx(AutocompleteItem, {
            active: index === activeIndex,
            item: result,
            onClick: () => handleResultSelect(result, index),
            onMouseEnter: () => setActiveIndex(-1)
          })
        }), `${debouncedSearchTerm}-${result.id}`))
      })]
    })]
  });
}
function AutocompleteItem({
  active,
  item,
  onClick,
  onMouseEnter
}) {
  var _item$base$query;
  const [hovered, setHovered] = useState(false);
  return /*#__PURE__*/_jsx(ListItem, {
    "aria-selected": active
    // @ts-expect-error `cursor` is supported actually
    ,
    cursor: "pointer",
    onMouseDown: onClick,
    onMouseLeave: () => setHovered(false),
    onMouseEnter: () => {
      setHovered(true);
      onMouseEnter();
    },
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      backgroundColor: active || hovered ? 'fill-surface-default-hover' : undefined,
      gap: "400",
      paddingBlock: "200",
      paddingInline: "400",
      children: [/*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        blockSize: "32px",
        inlineSize: "32px",
        justifyContent: "center",
        children: item.image
      }), /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        children: [/*#__PURE__*/_jsxs(Text, {
          typography: "heading-50",
          children: [item.base.term, ((_item$base$query = item.base.query) === null || _item$base$query === void 0 ? void 0 : _item$base$query.queryType) === 'LLM' && /*#__PURE__*/_jsx(Logo, {
            height: "16px",
            marginBlockStart: "-100",
            marginInlineStart: "200",
            name: "breeze-symbol-gradient",
            width: "16px"
          })]
        }), item.subtitle && /*#__PURE__*/_jsx(Text, {
          as: "small",
          color: "text-core-subtle",
          children: item.subtitle
        })]
      })]
    })
  });
}